import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { UploadBatchProductionReport } from '@models/ui/upload-batch-production-report.model';
import { UploadBatchValidationMetadataProperty } from '@models/upload-batch-validation-metadata-property';
import { delay, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { ProcessingManager } from 'src/app/managers/processing.manager';
import { ValidationReportManager } from 'src/app/managers/upload-batch-validation-report.manager';
import { UploadBatchReportBaseComponent } from '../upload-batch-report-base.component';
import { ComparisonUtility } from 'src/app/utilities/comparison.utility';
import { FolderUploadStepRequest } from '@models/ui/job-request/folder-upload-step-request.model';
import { UploadFolderDialogComponent } from '@dialogs/upload-folder-dialog/upload-folder-dialog.component';
import { Store } from "@ngrx/store";
import { selectIsProcessing } from "@store/global/global.selectors";
import { UploadBatchesSdk } from "@sdks/upload-batches.sdk";

@Component({
    selector: 'lex-production-upload-batch-report',
    templateUrl: './production-upload-batch-report.component.html',
    styleUrls: ['./production-upload-batch-report.component.scss'],
    standalone: false
})
export class ProductionUploadBatchReportComponent extends UploadBatchReportBaseComponent implements OnInit, OnDestroy {

	public hasMissingSourceFiles: boolean = false;
	public showRepairControls: boolean = false;
	public isProcessing = false;
	public isProductionType = false;

	constructor(public reportManager: ValidationReportManager,
				private route: ActivatedRoute,
				public router: Router,
				public matDialog: MatDialog,
				public processingManager: ProcessingManager,
				private store: Store,
				public uploadBatchesSdk: UploadBatchesSdk
	) {
		super(router, matDialog, processingManager, reportManager, uploadBatchesSdk);
		this.reportManager.currentProductionValidationReport$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			takeUntil(this.destroy$)
		).subscribe(batchValidation => {
			if (!batchValidation)
				return of(null);
			this.errorData = [];
			this.warningData = [];
			this.infoData = [];

			this.uploadBatchTitle = batchValidation.uploadBatchTitle;
			this.uploadedTimestamp = batchValidation.uploadedTimestamp;
			this.uploadBatchId = batchValidation.uploadBatchId;
			this.isProductionType = batchValidation.uploadBatchType.displayName == 'Production Upload';
			this.setupMetaTableData(batchValidation);
			this.uploadBatchStatus = batchValidation.validationStatus;

			if (batchValidation.validationData) {
				this.dateTimeRun = batchValidation.validationData.runReportDateTime;
				this.showErrors = batchValidation.validationData.hasErrors();
				this.showWarnings = batchValidation.validationData.hasWarnings();
				this.showInfo = batchValidation.validationData.hasInfo();
				this.hasMissingSourceFiles = batchValidation.validationData.missingSourceFileCount > 0;
				if (batchValidation.validationData.missingOriginalFileCount > 0)
					this.errorData.push({
						name: this.missingNativeFileLabel,
						value: batchValidation.validationData.missingOriginalFileCount.toString()
					});
				if (batchValidation.validationData.missingSourceFileCount > 0)
					this.errorData.push({
						name: this.missingSourceFileLabel,
						value: batchValidation.validationData.missingSourceFileCount.toString()
					});
				if (batchValidation.validationData.missingPdfFileCount > 0)
					this.errorData.push({
						name: this.missingNormalizedPdfLabel,
						value: batchValidation.validationData.missingPdfFileCount.toString()
					});
				if (batchValidation.validationData.failedToConvertCount > 0)
					this.errorData.push({
						name: this.failedToConvertLabel,
						value: batchValidation.validationData.failedToConvertCount.toString()
					});
				if (batchValidation.validationData.failedToIndexDocCount > 0)
					this.warningData.push({
						name: this.failedToIndexLabel,
						value: batchValidation.validationData.failedToIndexDocCount.toString()
					});
				if (batchValidation.validationData.pageCountThresholdCount > 0)
					this.infoData.push({
						name: this.pageCountLabel,
						value: batchValidation.validationData.pageCountThresholdCount.toString()
					});
				if (batchValidation.validationData.unsupportedFileTypeThresholdCount > 0)
					this.infoData.push({
						name: this.unsupportedFileLabel,
						value: batchValidation.validationData.unsupportedFileTypeThresholdCount.toString()
					});
			}
		});

		this.store.select(selectIsProcessing).pipe(
			takeUntil(this.destroy$)
		).subscribe((isProcessing) => {
			this.isProcessing = isProcessing;
		});
	}

	public ngOnInit(): void {
		this.refreshValidationData();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.reportManager.clearProductionUploadBatchReport();
	}

	public refreshValidationData(): void {
		this.reportManager.setCurrentProductionUploadBatchReport(this.route.snapshot.params?.id);
	}

	public queueRepairJob(): void {

		this.matDialog.open(UploadFolderDialogComponent).afterClosed().pipe(
			switchMap((data: { path: string, server: 'austin' | 'amazon' }) => {
				if (data) {
					const request = new FolderUploadStepRequest();
					request.stepParams.folderPath = data.path;
					request.stepParams.onlyNewFiles = true;
					request.stepParams.fileServer = data.server;
					request.stepParams.uploadBatchId = this.route.snapshot.params.id;

					return this.processingManager.scheduleStep({
						stepRequest: request
					}).pipe(
						delay(2000)
					);
				}
			})
		).subscribe(() => this.refreshValidationData());
	}

	private setupMetaTableData(data: UploadBatchProductionReport) {
		this.metaTableDataSource = new MatTableDataSource<UploadBatchValidationMetadataProperty>([
			{propertyName: 'Created', propertyValue: data.uploadedTimestamp},
			{propertyName: 'Batch Id', propertyValue: data.uploadBatchId},
			{propertyName: 'Batch Type', propertyValue: data.uploadBatchType.displayName},
			{propertyName: 'User E-mail', propertyValue: data.userEmail},
			{propertyName: 'Custodian', propertyValue: data.custodian},
			{propertyName: 'Document Source', propertyValue: data.docSource},
			{propertyName: 'Time Zone', propertyValue: data.timezone},
			{propertyName: 'Total Document Count', propertyValue: data.docCount},
			{propertyName: 'Page Count', propertyValue: data.totalPageCount}
		]);
	}
}
